import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client.js";
import { useSessions } from "../api/queries.js";
import { errorMessage } from "../lib/errors.js";
import { formatDateTime } from "../lib/format.js";
import { NavBar } from "../components/NavBar.js";
import { Empty } from "../components/Empty.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { useToast } from "../components/ToastContext.js";
import { DeviceIcon } from "../components/icons.js";
import { useState } from "react";

/**
 * 登录的设备（spec §5.1.2）。
 *
 * 「手机丢了」场景要的就是这个：撤销**除当前设备外**的全部会话。数据层只可能
 * 看到、只能撤销自己的会话 —— 撤销伴侣的会话不是一个功能，是越权。
 */
export function SessionsPage() {
  const sessions = useSessions();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: api.revokeOtherSessions,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      toast.show(
        data.revoked === 0 ? "没有其它设备需要登出" : `已登出 ${data.revoked} 台设备`,
      );
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <>
      <NavBar title="登录的设备" back />
      <div className="page">
        {sessions.isLoading && <Loading />}
        {sessions.isError && (
          <QueryError
            error={sessions.error}
            onRetry={() => void sessions.refetch()}
          />
        )}

        {error !== null && <div className="banner banner-error">{error}</div>}

        {sessions.data?.sessions.length === 0 && (
          <Empty
            icon={<DeviceIcon size={22} style={{ color: "var(--steel)" }} />}
            title="没有其它登录记录"
          />
        )}

        {sessions.data !== undefined && sessions.data.sessions.length > 0 && (
          <div className="list-card">
            {sessions.data.sessions.map((s) => {
              const current = s.id === sessions.data.current_session_id;
              return (
                <div key={s.id} className="setting-row">
                  <span className="set-ico sky">
                    <DeviceIcon size={17} />
                  </span>
                  <span className="set-txt">
                    <span className="t">
                      {s.userAgent.trim() === ""
                        ? "未知设备"
                        : shortUserAgent(s.userAgent)}
                      {current && (
                        <span
                          className="badge badge-shared"
                          style={{ marginLeft: 8 }}
                        >
                          这台设备
                        </span>
                      )}
                    </span>
                    <span className="d mono">
                      最近活跃 {formatDateTime(s.lastSeenAt)} · 登录于{" "}
                      {formatDateTime(s.createdAt)}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="btn btn-ghost btn-block"
          disabled={revoke.isPending}
          onClick={() => {
            setError(null);
            revoke.mutate();
          }}
        >
          {revoke.isPending ? "正在登出…" : "登出所有其他设备"}
        </button>
        <p className="foot-note">
          登出只影响会话，不影响写过的日记。当前这台设备会保留登录。
        </p>
      </div>
    </>
  );
}

/** 从 UA 里挑出可认的部分：太长会撑爆一行，且没人读得懂完整的 UA。 */
function shortUserAgent(ua: string): string {
  const browser = /(Edg|Chrome|Firefox|Safari)\/[\d.]+/.exec(ua)?.[1];
  const os = /(Windows NT|Mac OS X|Android|iPhone|iPad|Linux)/.exec(ua)?.[1];
  if (browser === undefined && os === undefined) return ua.slice(0, 40);
  return [os, browser].filter((x) => x !== undefined).join(" · ");
}
